import { BusMessage, MessageFactory, calcStringByteLength, UID_BYTES_PER_ELEMENT, calcTypeByteLength } from "flavr-common";
/**
 * Requests loading a script into the editor.
 */
export class EditorLoadScriptMessage extends BusMessage {
    get byteLength() {
        return calcStringByteLength(this.scriptName) + calcStringByteLength(this.scriptId);
    }
    constructor(scriptName, scriptId) {
        super();
        this.scriptName = scriptName;
        this.scriptId = scriptId;
    }
    serialization(stream) {
        this.scriptName = stream.utf16String(this.scriptName);
        this.scriptId = stream.utf16String(this.scriptId);
    }
    static is(msg) {
        return msg.type === EditorLoadScriptMessage.typestatic;
    }
}
EditorLoadScriptMessage.typestatic = "EditorLoadScriptMessage";
EditorLoadScriptMessage.subjectstatic = "general";
MessageFactory.register(EditorLoadScriptMessage);
/**
 * Requests a predefined model script.
 */
export class RequestPredefinedModelScriptMessage extends BusMessage {
    get byteLength() {
        return RequestPredefinedModelScriptMessage.byteLengthstatic;
    }
    serialization() { }
    static is(msg) {
        return msg.type === RequestPredefinedModelScriptMessage.typestatic;
    }
}
RequestPredefinedModelScriptMessage.typestatic = "RequestPredefinedModelScriptMessage";
RequestPredefinedModelScriptMessage.subjectstatic = "general";
RequestPredefinedModelScriptMessage.byteLengthstatic = 0;
MessageFactory.register(RequestPredefinedModelScriptMessage);
/**
 * Requests waiting until scripts were created and activated.
 */
export class AwaitScriptsActivationRequestMessage extends BusMessage {
    get byteLength() {
        return (UID_BYTES_PER_ELEMENT +
            Uint8Array.BYTES_PER_ELEMENT +
            Uint32Array.BYTES_PER_ELEMENT);
    }
    constructor(modelUid, includeDescendants = true, timeoutMs = 2000) {
        super();
        this.modelUid = modelUid;
        this.includeDescendants = includeDescendants;
        this.timeoutMs = timeoutMs;
    }
    serialization(stream) {
        this.modelUid = stream.uid(this.modelUid);
        this.includeDescendants = stream.boolean(this.includeDescendants);
        this.timeoutMs = stream.uint32(this.timeoutMs);
    }
    static is(msg) {
        return msg.type === AwaitScriptsActivationRequestMessage.typestatic;
    }
}
AwaitScriptsActivationRequestMessage.typestatic = "AwaitScriptsActivationRequestMessage";
AwaitScriptsActivationRequestMessage.subjectstatic = "general";
MessageFactory.register(AwaitScriptsActivationRequestMessage);
/**
 * Requests re-instantiating the scripts of the given models so they start again from their initial state.
 * Answered with `AskOkMessage` once the scripts were recreated and initialized.
 */
export class ResetModelScriptsRequestMessage extends BusMessage {
    get byteLength() {
        return (Uint32Array.BYTES_PER_ELEMENT + // modelUids length
            UID_BYTES_PER_ELEMENT * this.modelUids.length +
            Uint8Array.BYTES_PER_ELEMENT +
            Uint32Array.BYTES_PER_ELEMENT);
    }
    constructor(modelUids = [], includeDescendants = true, timeoutMs = 2000) {
        super();
        this.modelUids = modelUids;
        this.includeDescendants = includeDescendants;
        this.timeoutMs = timeoutMs;
    }
    serialization(stream) {
        if (stream.isReading) {
            this.modelUids = [];
        }
        const length = stream.uint32(this.modelUids.length);
        for (let i = 0; i < length; ++i) {
            this.modelUids[i] = stream.uid(this.modelUids[i]);
        }
        this.includeDescendants = stream.boolean(this.includeDescendants);
        this.timeoutMs = stream.uint32(this.timeoutMs);
    }
    static is(msg) {
        return msg.type === ResetModelScriptsRequestMessage.typestatic;
    }
}
ResetModelScriptsRequestMessage.typestatic = "ResetModelScriptsRequestMessage";
ResetModelScriptsRequestMessage.subjectstatic = "general";
MessageFactory.register(ResetModelScriptsRequestMessage);
/**
 * Returns the requested predefined script.
 */
export class ResponsePredefinedScriptMessage extends BusMessage {
    get byteLength() {
        return calcStringByteLength(this.script);
    }
    constructor(script) {
        super();
        this.script = script;
    }
    serialization(stream) {
        this.script = stream.utf16String(this.script);
    }
    static is(msg) {
        return msg.type === ResponsePredefinedScriptMessage.typestatic;
    }
}
ResponsePredefinedScriptMessage.typestatic = "ResponsePredefinedScriptMessage";
ResponsePredefinedScriptMessage.subjectstatic = "general";
MessageFactory.register(ResponsePredefinedScriptMessage);
/**
 * Announces that a script changed.
 */
export class ScriptChangedMessage extends BusMessage {
    get byteLength() {
        return calcStringByteLength(this.scriptId);
    }
    constructor(scriptId) {
        super();
        this.scriptId = scriptId;
    }
    serialization(stream) {
        this.scriptId = stream.utf16String(this.scriptId);
    }
    static is(msg) {
        return msg.type === ScriptChangedMessage.typestatic;
    }
}
ScriptChangedMessage.typestatic = "ScriptChangedMessage";
ScriptChangedMessage.subjectstatic = "general";
MessageFactory.register(ScriptChangedMessage);
/**
 * Invokes an RPC entry point on scripts.
 */
export class ScriptRPCMessage extends BusMessage {
    get byteLength() {
        return (UID_BYTES_PER_ELEMENT + // modelUid
            UID_BYTES_PER_ELEMENT + // scriptHash
            UID_BYTES_PER_ELEMENT + // funcNameHash
            calcTypeByteLength(this.params));
    }
    constructor(modelUid, scriptHash, funcNameHash, params = []) {
        super();
        this.modelUid = modelUid;
        this.scriptHash = scriptHash;
        this.funcNameHash = funcNameHash;
        this.params = params;
    }
    serialization(stream) {
        this.modelUid = stream.uid(this.modelUid);
        this.scriptHash = stream.uid(this.scriptHash);
        // ToDo funcName to uid
        this.funcNameHash = stream.uid(this.funcNameHash);
        if (stream.isReading) {
            this.params = [];
        }
        const paramsLength = stream.uint32(this.params.length);
        for (let i = 0; i < paramsLength; ++i) {
            this.params[i] = stream.type(this.params[i]);
        }
    }
    static is(msg) {
        return msg.type === ScriptRPCMessage.typestatic;
    }
}
ScriptRPCMessage.typestatic = "ScriptRPCMessage";
ScriptRPCMessage.subjectstatic = "peer";
MessageFactory.register(ScriptRPCMessage);
/**
 * Invokes a script RPC that should only run on the host.
 */
export class ScriptRPCOnlyHostMessage extends BusMessage {
    get byteLength() {
        return (UID_BYTES_PER_ELEMENT + // modelUid
            UID_BYTES_PER_ELEMENT + // scriptHash
            UID_BYTES_PER_ELEMENT + // funcNameHash
            calcTypeByteLength(this.params));
    }
    constructor(modelUid, scriptHash, funcNameHash, params = []) {
        super();
        this.modelUid = modelUid;
        this.scriptHash = scriptHash;
        this.funcNameHash = funcNameHash;
        this.params = params;
    }
    serialization(stream) {
        this.modelUid = stream.uid(this.modelUid);
        this.scriptHash = stream.uid(this.scriptHash);
        this.funcNameHash = stream.uid(this.funcNameHash);
        if (stream.isReading) {
            this.params = [];
        }
        const paramsLength = stream.uint32(this.params.length);
        for (let i = 0; i < paramsLength; ++i) {
            this.params[i] = stream.type(this.params[i]);
        }
    }
    static is(msg) {
        return msg.type === ScriptRPCOnlyHostMessage.typestatic;
    }
}
ScriptRPCOnlyHostMessage.typestatic = "ScriptRPCOnlyHostMessage";
ScriptRPCOnlyHostMessage.subjectstatic = "general";
MessageFactory.register(ScriptRPCOnlyHostMessage);
/**
 * Sends a script event to dynamic HTML content.
 */
export class DynamicHTMLScriptEventMessage extends BusMessage {
    get byteLength() {
        return (UID_BYTES_PER_ELEMENT + // roomUid
            UID_BYTES_PER_ELEMENT + // uid
            UID_BYTES_PER_ELEMENT + // patternHash
            calcTypeByteLength(this.params));
    }
    constructor(roomUid, uid, patternHash, params = []) {
        super();
        this.roomUid = roomUid;
        this.uid = uid;
        this.patternHash = patternHash;
        this.params = params;
    }
    serialization(stream) {
        this.roomUid = stream.uid(this.roomUid);
        this.uid = stream.uid(this.uid);
        this.patternHash = stream.uid(this.patternHash);
        if (stream.isReading) {
            this.params = [];
        }
        const paramsLength = stream.uint32(this.params.length);
        for (let i = 0; i < paramsLength; ++i) {
            this.params[i] = stream.type(this.params[i]);
        }
    }
    static is(msg) {
        return msg.type === DynamicHTMLScriptEventMessage.typestatic;
    }
}
DynamicHTMLScriptEventMessage.typestatic = "DynamicHTMLScriptEventMessage";
DynamicHTMLScriptEventMessage.subjectstatic = "general";
MessageFactory.register(DynamicHTMLScriptEventMessage);
/**
 * Returns the result of a dynamic HTML script event.
 */
export class ResponseDynamicHTMLScriptEventMessage extends BusMessage {
    get byteLength() {
        return calcTypeByteLength(this.value);
    }
    constructor(value) {
        super();
        this.value = value;
    }
    serialization(stream) {
        this.value = stream.type(this.value);
    }
    static is(msg) {
        return msg.type === ResponseDynamicHTMLScriptEventMessage.typestatic;
    }
}
ResponseDynamicHTMLScriptEventMessage.typestatic = "ResponseDynamicHTMLScriptEventMessage";
ResponseDynamicHTMLScriptEventMessage.subjectstatic = "general";
MessageFactory.register(ResponseDynamicHTMLScriptEventMessage);
/**
 * Sends a script event from runtime scripts to dynamic HTML.
 */
export class ScriptEventToDynamicHTMLMessage extends BusMessage {
    get byteLength() {
        return (UID_BYTES_PER_ELEMENT + // roomUid
            UID_BYTES_PER_ELEMENT + // patternHash
            calcTypeByteLength(this.params));
    }
    constructor(roomUid, patternHash, params = []) {
        super();
        this.roomUid = roomUid;
        this.patternHash = patternHash;
        this.params = params;
    }
    serialization(stream) {
        this.roomUid = stream.uid(this.roomUid);
        this.patternHash = stream.uid(this.patternHash);
        if (stream.isReading) {
            this.params = [];
        }
        const paramsLength = stream.uint32(this.params.length);
        for (let i = 0; i < paramsLength; ++i) {
            this.params[i] = stream.type(this.params[i]);
        }
    }
    static is(msg) {
        return msg.type === ScriptEventToDynamicHTMLMessage.typestatic;
    }
}
ScriptEventToDynamicHTMLMessage.typestatic = "ScriptEventToDynamicHTMLMessage";
ScriptEventToDynamicHTMLMessage.subjectstatic = "general";
MessageFactory.register(ScriptEventToDynamicHTMLMessage);
/**
 * Returns the result of sending a script event to dynamic HTML.
 */
export class ResponseScriptEventToDynamicHTMLMessage extends BusMessage {
    get byteLength() {
        return calcTypeByteLength(this.value);
    }
    constructor(value) {
        super();
        this.value = value;
    }
    serialization(stream) {
        this.value = stream.type(this.value);
    }
    static is(msg) {
        return msg.type === ResponseScriptEventToDynamicHTMLMessage.typestatic;
    }
}
ResponseScriptEventToDynamicHTMLMessage.typestatic = "ResponseScriptEventToDynamicHTMLMessage";
ResponseScriptEventToDynamicHTMLMessage.subjectstatic = "general";
MessageFactory.register(ResponseScriptEventToDynamicHTMLMessage);
export var HistoryEventType;
(function (HistoryEventType) {
    HistoryEventType[HistoryEventType["moisture"] = 1] = "moisture";
    HistoryEventType[HistoryEventType["distance"] = 2] = "distance";
    HistoryEventType[HistoryEventType["photo"] = 3] = "photo";
    HistoryEventType[HistoryEventType["modelUpload"] = 4] = "modelUpload";
    HistoryEventType[HistoryEventType["annotation"] = 5] = "annotation";
    HistoryEventType[HistoryEventType["chatMessage"] = 6] = "chatMessage";
    HistoryEventType[HistoryEventType["userJoined"] = 7] = "userJoined";
    HistoryEventType[HistoryEventType["userLeft"] = 8] = "userLeft";
    HistoryEventType[HistoryEventType["startRecording"] = 9] = "startRecording";
    HistoryEventType[HistoryEventType["stopRecording"] = 10] = "stopRecording";
})(HistoryEventType || (HistoryEventType = {}));
/**
 * Publishes a script-related history event.
 */
export class ScriptHistoryEventMessage extends BusMessage {
    get byteLength() {
        return (Uint8Array.BYTES_PER_ELEMENT + // description
            calcStringByteLength(this.params.value) +
            calcStringByteLength(this.params.clientId));
    }
    constructor(params = {}) {
        super();
        this.params = params;
    }
    serialization(stream) {
        if (stream.isReading) {
            this.params = {};
        }
        this.params.value = stream.utf16StringSafe(this.params.value);
        this.params.type = stream.uint8Safe(this.params.type);
        this.params.clientId = stream.utf16StringSafe(this.params.clientId);
    }
    static is(msg) {
        return msg.type === ScriptHistoryEventMessage.typestatic;
    }
}
ScriptHistoryEventMessage.typestatic = "ScriptHistoryEventMessage";
ScriptHistoryEventMessage.subjectstatic = "general";
MessageFactory.register(ScriptHistoryEventMessage);
/**
 * Announces that script creation finished.
 */
export class ScriptsCreationFinished extends BusMessage {
    get byteLength() {
        return UID_BYTES_PER_ELEMENT;
    }
    constructor(roomUid) {
        super();
        this.roomUid = roomUid;
    }
    serialization(stream) {
        this.roomUid = stream.uid(this.roomUid);
    }
    static is(msg) {
        return msg.type === ScriptHistoryEventMessage.typestatic;
    }
}
ScriptsCreationFinished.typestatic = "ScriptsCreationFinished";
ScriptsCreationFinished.subjectstatic = "general";
MessageFactory.register(ScriptsCreationFinished);
